// F-INVEST typed artifact substrate — the three shared scenario-investment
// evidence artifacts (#4602 Q1-FIX PR-A).
//
// Design authority: docs/capabilities/homework/finance-evidence-design-20260710.md
// §5 "Shared investment evidence" (lines 234-310). Its binding calls:
//
//   - "Finance must consume this contract rather than redefining costs." The
//     canonical cost/quantity/basis/timing rules stay in
//     scenario-investment-requirements.ts and are enforced HERE by DELEGATION —
//     parseScenarioInvestmentRequirementsV1 is called on the artifact's own
//     requirement rows, never re-implemented (Law 5, single source). This file
//     adds only the finance tie-out extension the design's "Required canonical
//     v2 extensions" table names.
//   - "A build lane should make its context/source admission module-neutral."
//     The context kinds below are namespaced `scenario_investment:*`, not
//     `decision_agent:finance:*` — the ma-synergy-commitment.ts precedent, where
//     packages/lib owns the literal and the module evidence contract imports it.
//   - "Replace the current pseudo-keys." The three artifact-level evidence keys
//     are declared here so the retiring keys have a typed successor. Registering
//     them in financeModuleEvidenceContract.ts entries is NOT done in this PR:
//     the batch-1 anti-invention guard
//     (apps/api/src/decision-modules/contribution-contract.conformance.spec.ts,
//     "every contract evidenceKey is declared by the module itself") requires the
//     module's own manifest to declare a key before a contract may name it, and
//     finance.manifest.ts declares none of the three yet. PR-B lands the manifest
//     declaration, the engine consumption, and the registration together.
//
// Versioning note: each artifact is at v1. The design calls its extension fields
// "canonical v2 extensions" because they extend ScenarioInvestmentRequirementsV1;
// that canonical contract itself stays at v1 and is consumed unchanged.
//
// Every parser is fail-closed and returns the input unchanged on success — no
// stamping, no defaulting, no coercion. A number that is not present is a gap,
// never a zero (design §1: "Unknown requirements remain typed named gaps; they
// never contribute zero to a total or ROI denominator").

import { DECISION_AGENT_SOURCE_CLASSES } from './contracts/sourceClasses';
import type { DecisionAgentSourceClass } from './contracts/sourceClasses';
import {
  SCENARIO_INVESTMENT_REQUIREMENTS_SCHEMA_VERSION,
  parseScenarioInvestmentRequirementsV1,
} from './scenario-investment-requirements';
import type { ScenarioInvestmentRequirementV1 } from './scenario-investment-requirements';

// --------------------------------------------------------------------------
// Contract identity (the literals a module evidence contract imports)
// --------------------------------------------------------------------------

export const SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS = {
  requirements: 'approved_scenario_investment_requirements_artifact',
  valueCase: 'approved_scenario_value_case_artifact',
  fundingPlan: 'approved_scenario_funding_plan_artifact',
} as const;

export const SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS = {
  requirements: 'scenario_investment:requirements_artifact',
  valueCase: 'scenario_investment:value_case_artifact',
  fundingPlan: 'scenario_investment:funding_plan_artifact',
} as const;

export const SCENARIO_INVESTMENT_REQUIREMENTS_ARTIFACT_SCHEMA_VERSION =
  'scenario_investment_requirements_artifact_v1' as const;

export const SCENARIO_VALUE_CASE_ARTIFACT_SCHEMA_VERSION =
  'scenario_value_case_artifact_v1' as const;

export const SCENARIO_FUNDING_PLAN_ARTIFACT_SCHEMA_VERSION =
  'scenario_funding_plan_artifact_v1' as const;

export const SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS = {
  requirements: SCENARIO_INVESTMENT_REQUIREMENTS_ARTIFACT_SCHEMA_VERSION,
  valueCase: SCENARIO_VALUE_CASE_ARTIFACT_SCHEMA_VERSION,
  fundingPlan: SCENARIO_FUNDING_PLAN_ARTIFACT_SCHEMA_VERSION,
} as const;

type ScenarioInvestmentArtifactEvidenceKeys =
  typeof SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS;
type ScenarioInvestmentArtifactContextKinds =
  typeof SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS;
type ScenarioInvestmentArtifactSchemaVersions =
  typeof SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS;

export type ScenarioInvestmentArtifactEvidenceKey =
  ScenarioInvestmentArtifactEvidenceKeys[keyof ScenarioInvestmentArtifactEvidenceKeys];

export type ScenarioInvestmentArtifactContextKind =
  ScenarioInvestmentArtifactContextKinds[keyof ScenarioInvestmentArtifactContextKinds];

export type ScenarioInvestmentArtifactSchemaVersion =
  ScenarioInvestmentArtifactSchemaVersions[keyof ScenarioInvestmentArtifactSchemaVersions];

// --------------------------------------------------------------------------
// Shared closed vocabularies
// --------------------------------------------------------------------------

export const SCENARIO_INVESTMENT_ACCOUNTING_CLASSIFICATIONS = [
  'opex',
  'capex',
  'working_capital',
  'non_cash',
] as const;

export const SCENARIO_INVESTMENT_DEPRECIATION_METHODS = [
  'straight_line',
  'declining_balance',
  'units_of_production',
] as const;

export const SCENARIO_INVESTMENT_PNL_EFFECT_TYPES = [
  'opex',
  'depreciation',
  'amortization',
] as const;

export const SCENARIO_VALUE_DIRECTIONS = ['increase', 'decrease'] as const;

// Closed BY DESIGN to the two non-observed labels: design §5 — "Forecast/
// assumption values are labeled as such; they are never observed actuals."
// There is deliberately no 'observed' member, so an expected incremental value
// cannot be labeled as an actual.
export const SCENARIO_VALUE_CASE_VALUE_CLASSES = ['forecast', 'assumption'] as const;

export const SCENARIO_VALUE_CASE_RISK_ADJUSTMENT_KINDS = [
  'realization_probability',
  'risk_adjustment_factor',
] as const;

export const SCENARIO_FUNDING_SOURCE_KINDS = [
  'budget',
  'capital_plan',
  'facility',
  'cash',
] as const;

export const SCENARIO_FUNDING_RESTRICTION_KINDS = ['unrestricted', 'restricted'] as const;

export type ScenarioInvestmentAccountingClassification =
  (typeof SCENARIO_INVESTMENT_ACCOUNTING_CLASSIFICATIONS)[number];

export type ScenarioInvestmentDepreciationMethod =
  (typeof SCENARIO_INVESTMENT_DEPRECIATION_METHODS)[number];

export type ScenarioInvestmentPnlEffectType =
  (typeof SCENARIO_INVESTMENT_PNL_EFFECT_TYPES)[number];

export type ScenarioValueDirection = (typeof SCENARIO_VALUE_DIRECTIONS)[number];

export type ScenarioValueCaseValueClass = (typeof SCENARIO_VALUE_CASE_VALUE_CLASSES)[number];

export type ScenarioValueCaseRiskAdjustmentKind =
  (typeof SCENARIO_VALUE_CASE_RISK_ADJUSTMENT_KINDS)[number];

export type ScenarioFundingSourceKind = (typeof SCENARIO_FUNDING_SOURCE_KINDS)[number];

export type ScenarioFundingRestrictionKind =
  (typeof SCENARIO_FUNDING_RESTRICTION_KINDS)[number];

// --------------------------------------------------------------------------
// Shared provenance envelope (design §1 evidence laws)
// --------------------------------------------------------------------------

export type ScenarioInvestmentArtifactOwner = Readonly<{
  ownerRole: string;
  ownerId?: string | null;
}>;

export type ScenarioInvestmentArtifactApproval = Readonly<{
  approvedBy: string;
  approvedAt: string;
  approvalRef: string;
}>;

export type ScenarioInvestmentArtifactProvenance = Readonly<{
  tenantId: string;
  companyId: string;
  strategyId: string;
  scenarioId: string;
  asOf: string;
  sourceClass: DecisionAgentSourceClass;
  sourceRef: string;
  /** Freshness anchor: the SOURCE fact's as-of, never artifact creation time. */
  sourceAsOf: string;
  owner: ScenarioInvestmentArtifactOwner;
  approval: ScenarioInvestmentArtifactApproval;
}>;

// --------------------------------------------------------------------------
// 1. approved_scenario_investment_requirements_artifact
// --------------------------------------------------------------------------

export type ScenarioInvestmentCashFlowRow = Readonly<{
  fiscalPeriod: string;
  amountUsd: number;
  currency: 'USD';
}>;

export type ScenarioInvestmentPnlEffectRow = Readonly<{
  fiscalPeriod: string;
  amountUsd: number;
  currency: 'USD';
  effectType: ScenarioInvestmentPnlEffectType;
}>;

/**
 * The design's "Required canonical v2 extensions" table, field for field. It is
 * an extension ONLY: nothing here restates a cost, quantity, basis, rung, or
 * timing already governed by ScenarioInvestmentRequirementV1.
 */
export type ScenarioInvestmentRequirementFinanceExtension = Readonly<{
  initiativeId: string;
  /** Null for a unique requirement; set groups shared capability/platform cost. */
  requirementGroupId: string | null;
  accountingClassification: ScenarioInvestmentAccountingClassification;
  glAccountIds: readonly string[];
  costPoolIds: readonly string[];
  projectIds: readonly string[];
  orgNodeIds: readonly string[];
  vendorContractIds: readonly string[];
  /** Empty ONLY on a named-cost-gap row, where scheduleGapRef states the absence. */
  cashFlowSchedule: readonly ScenarioInvestmentCashFlowRow[];
  /** Empty on a named-cost-gap row, or on a costed working_capital row. */
  pnlEffectSchedule: readonly ScenarioInvestmentPnlEffectRow[];
  /**
   * Typed schedule absence: the canonical `namedCostGap.gapId` these empty
   * schedules belong to. Required when the row carries a namedCostGap instead
   * of a governed costUsd, forbidden otherwise — the same required-when/
   * forbidden-otherwise idiom as `overlapReceiptRef` and the funding plan's
   * `explicitZeroBasis`. A schedule is never absent by silence.
   */
  scheduleGapRef: string | null;
  capitalizationPolicyRef: string | null;
  usefulLifePeriods: number | null;
  depreciationMethod: ScenarioInvestmentDepreciationMethod | null;
  /** Required when requirementGroupId is set (shared resource across initiatives). */
  overlapReceiptRef: string | null;
  sourceAsOf: string;
  evidenceRef: string;
  proofRef: string;
}>;

export type ScenarioInvestmentRequirementArtifactRowV1 = ScenarioInvestmentRequirementV1 &
  ScenarioInvestmentRequirementFinanceExtension;

export type ScenarioInvestmentRequirementsArtifactV1 = ScenarioInvestmentArtifactProvenance &
  Readonly<{
    schemaVersion: typeof SCENARIO_INVESTMENT_REQUIREMENTS_ARTIFACT_SCHEMA_VERSION;
    moduleId: string;
    requirements: readonly ScenarioInvestmentRequirementArtifactRowV1[];
  }>;

export type ScenarioInvestmentRequirementsArtifactParseResult =
  | Readonly<{ success: true; data: ScenarioInvestmentRequirementsArtifactV1 }>
  | Readonly<{ success: false; error: Error }>;

export function parseScenarioInvestmentRequirementsArtifactV1(
  input: unknown,
): ScenarioInvestmentRequirementsArtifactV1 {
  const record = requireRecord(input, 'ScenarioInvestmentRequirementsArtifactV1');
  requireSchemaVersion(
    record.schemaVersion,
    SCENARIO_INVESTMENT_REQUIREMENTS_ARTIFACT_SCHEMA_VERSION,
    'ScenarioInvestmentRequirementsArtifactV1',
  );
  validateProvenance(record, 'ScenarioInvestmentRequirementsArtifactV1');
  const moduleId = requireString(record.moduleId, 'moduleId');

  // Canonical delegation: the cost/quantity/basis/rung/timing/capability rules
  // are the canonical contract's, executed by the canonical parser. The
  // projection carries the canonical schemaVersion because the ARTIFACT owns its
  // own envelope version; the rows themselves are canonical requirement rows.
  parseScenarioInvestmentRequirementsV1({
    schemaVersion: SCENARIO_INVESTMENT_REQUIREMENTS_SCHEMA_VERSION,
    scenarioId: record.scenarioId,
    moduleId,
    asOf: record.asOf,
    requirements: record.requirements,
  });

  const requirements = requireArray(record.requirements, 'requirements');
  const seenRequirementIds = new Set<string>();
  requirements.forEach((requirement, index) => {
    const path = `requirements[${index}]`;
    const row = requireRecord(requirement, path);
    const requirementId = requireString(row.requirementId, `${path}.requirementId`);
    if (seenRequirementIds.has(requirementId)) {
      throw new Error(`${path}.requirementId '${requirementId}' is declared more than once.`);
    }
    seenRequirementIds.add(requirementId);
    validateRequirementFinanceExtension(row, path);
  });

  return input as ScenarioInvestmentRequirementsArtifactV1;
}

export const ScenarioInvestmentRequirementsArtifactV1Schema = {
  parse(input: unknown): ScenarioInvestmentRequirementsArtifactV1 {
    return parseScenarioInvestmentRequirementsArtifactV1(input);
  },
  safeParse(input: unknown): ScenarioInvestmentRequirementsArtifactParseResult {
    return safeParse(() => parseScenarioInvestmentRequirementsArtifactV1(input));
  },
};

function validateRequirementFinanceExtension(
  row: Record<string, unknown>,
  path: string,
): void {
  requireString(row.initiativeId, `${path}.initiativeId`);
  const requirementGroupId = requireNullableString(
    row.requirementGroupId,
    `${path}.requirementGroupId`,
  );

  const classification = requireEnum(
    row.accountingClassification,
    SCENARIO_INVESTMENT_ACCOUNTING_CLASSIFICATIONS,
    `${path}.accountingClassification`,
  );

  for (const key of [
    'glAccountIds',
    'costPoolIds',
    'projectIds',
    'orgNodeIds',
    'vendorContractIds',
  ] as const) {
    requireStringArray(row[key], `${path}.${key}`);
  }

  // ----------------------------------------------------------------------
  // Schedules — governed dollars when there ARE governed dollars, typed
  // absence when there are not (#4716 amendment).
  //
  // The canonical contract admits a requirement whose cost is a named gap:
  // validateRequirement in scenario-investment-requirements.ts enforces "must
  // carry exactly one of costUsd or namedCostGap", and a named_gap rung row
  // carries namedCostGap with NO costUsd at all. Such a row has no governed
  // dollar amount, therefore no governed dollars to spread over fiscal
  // periods. Demanding a non-empty cash or P&L schedule from it would force
  // the author to INVENT schedule dollars for a cost nobody has evidenced —
  // the fabrication design §1 forbids ("Unknown requirements remain typed
  // named gaps; they never contribute zero to a total or ROI denominator").
  //
  // So the absence is declared, never defaulted: a named-cost-gap row carries
  // empty schedules plus scheduleGapRef naming the canonical gap they belong
  // to. A governed-cost row keeps the original non-empty rules unchanged and
  // may not declare a schedule gap at all.
  const namedCostGap =
    row.namedCostGap === undefined || row.namedCostGap === null
      ? null
      : requireRecord(row.namedCostGap, `${path}.namedCostGap`);
  const scheduleGapRef = requireNullableString(row.scheduleGapRef, `${path}.scheduleGapRef`);

  const cashFlowSchedule = requireArray(row.cashFlowSchedule, `${path}.cashFlowSchedule`);
  const pnlEffectSchedule = requireArray(row.pnlEffectSchedule, `${path}.pnlEffectSchedule`);

  if (namedCostGap) {
    if (!scheduleGapRef) {
      throw new Error(
        `${path}.scheduleGapRef is required for a named-cost-gap requirement.`,
      );
    }
    if (scheduleGapRef !== namedCostGap.gapId) {
      throw new Error(
        `${path}.scheduleGapRef must equal the canonical namedCostGap.gapId.`,
      );
    }
    // A named cost gap is the ABSENCE of a governed cost, so there is nothing
    // to schedule. Any USD here would be a number with no governed computation
    // behind it, which the anti-fabrication fence forbids. Partial schedules
    // are therefore unlawful too, not merely discouraged.
    if (cashFlowSchedule.length > 0 || pnlEffectSchedule.length > 0) {
      throw new Error(
        `${path} cannot schedule dollars for a named-cost-gap requirement.`,
      );
    }
  } else {
    if (scheduleGapRef) {
      throw new Error(
        `${path}.scheduleGapRef cannot be set on a requirement with governed costUsd.`,
      );
    }
    // "Required funding timing" — a governed cost with no cash timing cannot be
    // funded, so an empty schedule is a gap, never an implicit zero.
    if (cashFlowSchedule.length === 0) {
      throw new Error(`${path}.cashFlowSchedule must not be empty.`);
    }
    // working_capital is the one classification with no P&L line of its own;
    // every other classification has a stated P&L effect the design requires
    // typed.
    if (pnlEffectSchedule.length === 0 && classification !== 'working_capital') {
      throw new Error(
        `${path}.pnlEffectSchedule must not be empty for ${classification} requirements.`,
      );
    }
  }

  const cashPeriods = new Set<string>();
  cashFlowSchedule.forEach((entry, index) => {
    const rowPath = `${path}.cashFlowSchedule[${index}]`;
    const scheduleRow = requireRecord(entry, rowPath);
    const fiscalPeriod = requireString(scheduleRow.fiscalPeriod, `${rowPath}.fiscalPeriod`);
    requireFiniteNumber(scheduleRow.amountUsd, `${rowPath}.amountUsd`);
    requireUsd(scheduleRow.currency, `${rowPath}.currency`);
    if (cashPeriods.has(fiscalPeriod)) {
      throw new Error(
        `${rowPath}.fiscalPeriod '${fiscalPeriod}' repeats the requirement x period grain.`,
      );
    }
    cashPeriods.add(fiscalPeriod);
  });

  const pnlPeriods = new Set<string>();
  pnlEffectSchedule.forEach((entry, index) => {
    const rowPath = `${path}.pnlEffectSchedule[${index}]`;
    const scheduleRow = requireRecord(entry, rowPath);
    const fiscalPeriod = requireString(scheduleRow.fiscalPeriod, `${rowPath}.fiscalPeriod`);
    requireFiniteNumber(scheduleRow.amountUsd, `${rowPath}.amountUsd`);
    requireUsd(scheduleRow.currency, `${rowPath}.currency`);
    requireEnum(
      scheduleRow.effectType,
      SCENARIO_INVESTMENT_PNL_EFFECT_TYPES,
      `${rowPath}.effectType`,
    );
    if (pnlPeriods.has(fiscalPeriod)) {
      throw new Error(
        `${rowPath}.fiscalPeriod '${fiscalPeriod}' repeats the requirement x period grain.`,
      );
    }
    pnlPeriods.add(fiscalPeriod);
  });

  const capitalizationPolicyRef = requireNullableString(
    row.capitalizationPolicyRef,
    `${path}.capitalizationPolicyRef`,
  );
  const usefulLifePeriods = requireNullableFiniteNumber(
    row.usefulLifePeriods,
    `${path}.usefulLifePeriods`,
    { min: 0, exclusiveMin: true },
  );
  const depreciationMethod =
    row.depreciationMethod === undefined || row.depreciationMethod === null
      ? null
      : requireEnum(
          row.depreciationMethod,
          SCENARIO_INVESTMENT_DEPRECIATION_METHODS,
          `${path}.depreciationMethod`,
        );

  // "No guessed capitalization" — capex without policy, life, and method cannot
  // have its P&L effect derived, so it fails closed rather than being assumed.
  if (classification === 'capex') {
    if (!capitalizationPolicyRef) {
      throw new Error(`${path}.capitalizationPolicyRef is required for capex requirements.`);
    }
    if (usefulLifePeriods === null) {
      throw new Error(`${path}.usefulLifePeriods is required for capex requirements.`);
    }
    if (depreciationMethod === null) {
      throw new Error(`${path}.depreciationMethod is required for capex requirements.`);
    }
  }

  const overlapReceiptRef = requireNullableString(
    row.overlapReceiptRef,
    `${path}.overlapReceiptRef`,
  );
  if (requirementGroupId && !overlapReceiptRef) {
    throw new Error(
      `${path}.overlapReceiptRef is required when requirementGroupId is set.`,
    );
  }

  requireString(row.sourceAsOf, `${path}.sourceAsOf`);
  requireString(row.evidenceRef, `${path}.evidenceRef`);
  requireString(row.proofRef, `${path}.proofRef`);
}

// --------------------------------------------------------------------------
// 2. approved_scenario_value_case_artifact
// --------------------------------------------------------------------------

export type ScenarioValueCaseBaseline = Readonly<{
  metricKey: string;
  value: number;
  unit: string;
  period: string;
  scope: string;
  evidenceRef: string;
}>;

export type ScenarioValueCaseExpectedValue = Readonly<{
  amount: number;
  unit: string;
  valueClass: ScenarioValueCaseValueClass;
  basisRef: string;
}>;

export type ScenarioValueCaseRiskAdjustment = Readonly<{
  kind: ScenarioValueCaseRiskAdjustmentKind;
  value: number;
  basisRef: string;
  description: string;
}>;

/**
 * Grain: one scenario (the artifact envelope) x initiative x fiscal period x
 * value metric. The design's "gross benefit cash/P&L schedule" IS this row set
 * read across fiscal periods — nesting a per-period array inside a per-period
 * row would declare the period twice and break the stated grain.
 */
export type ScenarioValueCaseRowV1 = Readonly<{
  initiativeId: string;
  fiscalPeriod: string;
  valueMetricKey: string;
  baseline: ScenarioValueCaseBaseline;
  expectedIncrementalValue: ScenarioValueCaseExpectedValue;
  valueDirection: ScenarioValueDirection;
  grossBenefitCashUsd: number;
  grossBenefitPnlUsd: number;
  riskAdjustment: ScenarioValueCaseRiskAdjustment;
  taxEffectUsd: number | null;
  workingCapitalEffectUsd: number | null;
  capexEffectUsd: number | null;
  overlapKeys: readonly string[];
  /** Required when overlapKeys is non-empty. */
  overlapReceiptRef: string | null;
  formulaRefs: readonly string[];
}>;

export type ScenarioValueCaseArtifactV1 = ScenarioInvestmentArtifactProvenance &
  Readonly<{
    schemaVersion: typeof SCENARIO_VALUE_CASE_ARTIFACT_SCHEMA_VERSION;
    rows: readonly ScenarioValueCaseRowV1[];
  }>;

export type ScenarioValueCaseArtifactParseResult =
  | Readonly<{ success: true; data: ScenarioValueCaseArtifactV1 }>
  | Readonly<{ success: false; error: Error }>;

export function parseScenarioValueCaseArtifactV1(input: unknown): ScenarioValueCaseArtifactV1 {
  const record = requireRecord(input, 'ScenarioValueCaseArtifactV1');
  requireSchemaVersion(
    record.schemaVersion,
    SCENARIO_VALUE_CASE_ARTIFACT_SCHEMA_VERSION,
    'ScenarioValueCaseArtifactV1',
  );
  validateProvenance(record, 'ScenarioValueCaseArtifactV1');

  const rows = requireArray(record.rows, 'rows');
  if (rows.length === 0) {
    throw new Error('ScenarioValueCaseArtifactV1 rows must not be empty.');
  }

  const seenGrainKeys = new Set<string>();
  rows.forEach((entry, index) => {
    const path = `rows[${index}]`;
    const row = requireRecord(entry, path);
    const initiativeId = requireString(row.initiativeId, `${path}.initiativeId`);
    const fiscalPeriod = requireString(row.fiscalPeriod, `${path}.fiscalPeriod`);
    const valueMetricKey = requireString(row.valueMetricKey, `${path}.valueMetricKey`);
    const grainKey = `${initiativeId}|${fiscalPeriod}|${valueMetricKey}`;
    if (seenGrainKeys.has(grainKey)) {
      throw new Error(`${path} repeats the initiative x period x value metric grain.`);
    }
    seenGrainKeys.add(grainKey);

    const baseline = requireRecord(row.baseline, `${path}.baseline`);
    requireString(baseline.metricKey, `${path}.baseline.metricKey`);
    requireFiniteNumber(baseline.value, `${path}.baseline.value`);
    requireString(baseline.unit, `${path}.baseline.unit`);
    requireString(baseline.period, `${path}.baseline.period`);
    requireString(baseline.scope, `${path}.baseline.scope`);
    requireString(baseline.evidenceRef, `${path}.baseline.evidenceRef`);

    const expected = requireRecord(
      row.expectedIncrementalValue,
      `${path}.expectedIncrementalValue`,
    );
    requireFiniteNumber(expected.amount, `${path}.expectedIncrementalValue.amount`);
    requireString(expected.unit, `${path}.expectedIncrementalValue.unit`);
    requireEnum(
      expected.valueClass,
      SCENARIO_VALUE_CASE_VALUE_CLASSES,
      `${path}.expectedIncrementalValue.valueClass`,
    );
    requireString(expected.basisRef, `${path}.expectedIncrementalValue.basisRef`);

    requireEnum(row.valueDirection, SCENARIO_VALUE_DIRECTIONS, `${path}.valueDirection`);
    requireFiniteNumber(row.grossBenefitCashUsd, `${path}.grossBenefitCashUsd`);
    requireFiniteNumber(row.grossBenefitPnlUsd, `${path}.grossBenefitPnlUsd`);

    validateRiskAdjustment(row.riskAdjustment, `${path}.riskAdjustment`);

    requireNullableFiniteNumber(row.taxEffectUsd, `${path}.taxEffectUsd`);
    requireNullableFiniteNumber(row.workingCapitalEffectUsd, `${path}.workingCapitalEffectUsd`);
    requireNullableFiniteNumber(row.capexEffectUsd, `${path}.capexEffectUsd`);

    const overlapKeys = requireStringArray(row.overlapKeys, `${path}.overlapKeys`);
    const overlapReceiptRef = requireNullableString(
      row.overlapReceiptRef,
      `${path}.overlapReceiptRef`,
    );
    if (overlapKeys.length > 0 && !overlapReceiptRef) {
      throw new Error(`${path}.overlapReceiptRef is required when overlapKeys is non-empty.`);
    }

    const formulaRefs = requireStringArray(row.formulaRefs, `${path}.formulaRefs`);
    if (formulaRefs.length === 0) {
      throw new Error(`${path}.formulaRefs must name at least one approved formula.`);
    }
  });

  return input as ScenarioValueCaseArtifactV1;
}

export const ScenarioValueCaseArtifactV1Schema = {
  parse(input: unknown): ScenarioValueCaseArtifactV1 {
    return parseScenarioValueCaseArtifactV1(input);
  },
  safeParse(input: unknown): ScenarioValueCaseArtifactParseResult {
    return safeParse(() => parseScenarioValueCaseArtifactV1(input));
  },
};

function validateRiskAdjustment(value: unknown, path: string): void {
  const record = requireRecord(value, path);
  const kind = requireEnum(
    record.kind,
    SCENARIO_VALUE_CASE_RISK_ADJUSTMENT_KINDS,
    `${path}.kind`,
  );
  const adjustment = requireFiniteNumber(record.value, `${path}.value`, { min: 0 });
  if (kind === 'realization_probability' && adjustment > 1) {
    throw new Error(`${path}.value must be a probability between 0 and 1.`);
  }
  // "with approved basis" — a risk adjustment with no basis is an unbound haircut.
  requireString(record.basisRef, `${path}.basisRef`);
  requireString(record.description, `${path}.description`);
}

// --------------------------------------------------------------------------
// 3. approved_scenario_funding_plan_artifact
// --------------------------------------------------------------------------

/** Grain: one scenario (the artifact envelope) x funding source x fiscal period. */
export type ScenarioFundingPlanRowV1 = Readonly<{
  fundingSourceId: string;
  fundingSourceKind: ScenarioFundingSourceKind;
  fiscalPeriod: string;
  approvedAmountUsd: number;
  currency: 'USD';
  restrictionKind: ScenarioFundingRestrictionKind;
  restrictionDescription: string | null;
  /** Required when approvedAmountUsd is 0, forbidden otherwise. */
  explicitZeroBasis: string | null;
  owner: ScenarioInvestmentArtifactOwner;
  approvalRef: string;
  sourceRef: string;
  sourceAsOf: string;
  decisionLedgerBindingRef: string;
}>;

export type ScenarioFundingPlanArtifactV1 = ScenarioInvestmentArtifactProvenance &
  Readonly<{
    schemaVersion: typeof SCENARIO_FUNDING_PLAN_ARTIFACT_SCHEMA_VERSION;
    rows: readonly ScenarioFundingPlanRowV1[];
  }>;

export type ScenarioFundingPlanArtifactParseResult =
  | Readonly<{ success: true; data: ScenarioFundingPlanArtifactV1 }>
  | Readonly<{ success: false; error: Error }>;

export function parseScenarioFundingPlanArtifactV1(
  input: unknown,
): ScenarioFundingPlanArtifactV1 {
  const record = requireRecord(input, 'ScenarioFundingPlanArtifactV1');
  requireSchemaVersion(
    record.schemaVersion,
    SCENARIO_FUNDING_PLAN_ARTIFACT_SCHEMA_VERSION,
    'ScenarioFundingPlanArtifactV1',
  );
  validateProvenance(record, 'ScenarioFundingPlanArtifactV1');

  const rows = requireArray(record.rows, 'rows');
  if (rows.length === 0) {
    throw new Error('ScenarioFundingPlanArtifactV1 rows must not be empty.');
  }

  const seenGrainKeys = new Set<string>();
  rows.forEach((entry, index) => {
    const path = `rows[${index}]`;
    const row = requireRecord(entry, path);
    const fundingSourceId = requireString(row.fundingSourceId, `${path}.fundingSourceId`);
    requireEnum(row.fundingSourceKind, SCENARIO_FUNDING_SOURCE_KINDS, `${path}.fundingSourceKind`);
    const fiscalPeriod = requireString(row.fiscalPeriod, `${path}.fiscalPeriod`);
    const grainKey = `${fundingSourceId}|${fiscalPeriod}`;
    if (seenGrainKeys.has(grainKey)) {
      throw new Error(`${path} repeats the funding source x period grain.`);
    }
    seenGrainKeys.add(grainKey);

    const approvedAmountUsd = requireFiniteNumber(
      row.approvedAmountUsd,
      `${path}.approvedAmountUsd`,
      { min: 0 },
    );
    requireUsd(row.currency, `${path}.currency`);

    const restrictionKind = requireEnum(
      row.restrictionKind,
      SCENARIO_FUNDING_RESTRICTION_KINDS,
      `${path}.restrictionKind`,
    );
    const restrictionDescription = requireNullableString(
      row.restrictionDescription,
      `${path}.restrictionDescription`,
    );
    if (restrictionKind === 'restricted' && !restrictionDescription) {
      throw new Error(
        `${path}.restrictionDescription is required for a restricted funding source.`,
      );
    }

    // "A governed explicit-zero row is required to assert no approved funding":
    // a zero must be asserted with its basis, never defaulted; and a funded row
    // may not carry a zero assertion.
    const explicitZeroBasis = requireNullableString(
      row.explicitZeroBasis,
      `${path}.explicitZeroBasis`,
    );
    if (approvedAmountUsd === 0 && !explicitZeroBasis) {
      throw new Error(
        `${path}.explicitZeroBasis is required to assert zero approved funding.`,
      );
    }
    if (approvedAmountUsd > 0 && explicitZeroBasis) {
      throw new Error(
        `${path}.explicitZeroBasis cannot be set on a row with approved funding.`,
      );
    }

    validateOwner(row.owner, `${path}.owner`);
    requireString(row.approvalRef, `${path}.approvalRef`);
    requireString(row.sourceRef, `${path}.sourceRef`);
    requireString(row.sourceAsOf, `${path}.sourceAsOf`);
    requireString(row.decisionLedgerBindingRef, `${path}.decisionLedgerBindingRef`);
  });

  return input as ScenarioFundingPlanArtifactV1;
}

export const ScenarioFundingPlanArtifactV1Schema = {
  parse(input: unknown): ScenarioFundingPlanArtifactV1 {
    return parseScenarioFundingPlanArtifactV1(input);
  },
  safeParse(input: unknown): ScenarioFundingPlanArtifactParseResult {
    return safeParse(() => parseScenarioFundingPlanArtifactV1(input));
  },
};

// --------------------------------------------------------------------------
// Shared validation (private — the canonical requirement rules are delegated,
// never re-implemented; these are primitive shape checks only)
// --------------------------------------------------------------------------

function validateProvenance(record: Record<string, unknown>, contractName: string): void {
  requireString(record.tenantId, 'tenantId');
  requireString(record.companyId, 'companyId');
  requireString(record.strategyId, 'strategyId');
  requireString(record.scenarioId, 'scenarioId');
  requireString(record.asOf, 'asOf');
  const sourceClass = requireString(record.sourceClass, 'sourceClass');
  const admitted: readonly string[] = Object.values(DECISION_AGENT_SOURCE_CLASSES);
  if (!admitted.includes(sourceClass)) {
    throw new Error(
      `${contractName} sourceClass '${sourceClass}' is not a governed source class.`,
    );
  }
  requireString(record.sourceRef, 'sourceRef');
  requireString(record.sourceAsOf, 'sourceAsOf');
  validateOwner(record.owner, 'owner');
  const approval = requireRecord(record.approval, 'approval');
  requireString(approval.approvedBy, 'approval.approvedBy');
  requireString(approval.approvedAt, 'approval.approvedAt');
  requireString(approval.approvalRef, 'approval.approvalRef');
}

function validateOwner(value: unknown, path: string): void {
  const record = requireRecord(value, path);
  requireString(record.ownerRole, `${path}.ownerRole`);
  requireNullableString(record.ownerId, `${path}.ownerId`);
}

function safeParse<T>(parse: () => T): Readonly<{ success: true; data: T }> | Readonly<{
  success: false;
  error: Error;
}> {
  try {
    return { success: true, data: parse() };
  } catch (error: unknown) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

function requireSchemaVersion(value: unknown, expected: string, contractName: string): void {
  const schemaVersion = requireString(value, 'schemaVersion');
  if (schemaVersion !== expected) {
    throw new Error(`Invalid ${contractName} schemaVersion '${schemaVersion}'.`);
  }
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${path} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function requireArray(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${path} must be an array.`);
  }
  return value;
}

function requireStringArray(value: unknown, path: string): readonly string[] {
  const values = requireArray(value, path);
  values.forEach((entry, index) => requireString(entry, `${path}[${index}]`));
  return values as readonly string[];
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${path} is required.`);
  }
  return value;
}

function requireNullableString(value: unknown, path: string): string | null {
  if (value === undefined || value === null) return null;
  return requireString(value, path);
}

function requireUsd(value: unknown, path: string): void {
  if (value !== 'USD') {
    throw new Error(`${path} must be USD.`);
  }
}

function requireFiniteNumber(
  value: unknown,
  path: string,
  opts: Readonly<{ min?: number; exclusiveMin?: boolean }> = {},
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${path} must be a finite number.`);
  }
  if (opts.min !== undefined) {
    const valid = opts.exclusiveMin ? value > opts.min : value >= opts.min;
    if (!valid) {
      const comparator = opts.exclusiveMin ? 'greater than' : 'greater than or equal to';
      throw new Error(`${path} must be ${comparator} ${opts.min}.`);
    }
  }
  return value;
}

function requireNullableFiniteNumber(
  value: unknown,
  path: string,
  opts: Readonly<{ min?: number; exclusiveMin?: boolean }> = {},
): number | null {
  if (value === undefined || value === null) return null;
  return requireFiniteNumber(value, path, opts);
}

function requireEnum<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    throw new Error(`${path} must be one of: ${allowed.join(', ')}.`);
  }
  return value as T;
}
